// src/pages/DashboardPage.js
import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Briefcase, 
  Clock, 
  FileText, 
  File, 
  Folder,
  ArrowRight,
  CircleAlert
} from 'lucide-react';
import { 
  customersAPI, 
  projectsAPI, 
  timeEntriesAPI, 
  reportsAPI, 
  offersAPI,
  invoicesAPI
} from '../services/api';

const StatCard = ({ icon: Icon, title, value, link, color }) => (
  <div className="card-modern p-6 group active:scale-[0.98] transform transition-all">
    <div className="flex justify-between items-start">
      <div className="flex-1">
        <div className="flex items-center mb-4">
          <div className={`p-2.5 rounded-xl ${color} bg-opacity-10`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
          <h3 className="ml-3 text-sm font-semibold text-gray-600 uppercase tracking-wide">{title}</h3>
        </div>
        <p className="text-4xl font-bold text-gray-900">{value}</p>
      </div>
      <a 
        href={link} 
        className="touch-target flex items-center justify-center text-primary-600 hover:text-primary-700 opacity-0 group-hover:opacity-100 transition-all"
      >
        <ArrowRight className="h-5 w-5" />
      </a>
    </div>
  </div>
);

const DashboardPage = () => {
  const [stats, setStats] = useState({
    customers: 0,
    projects: 0,
    activeTimeEntries: 0,
    pendingReports: 0,
    pendingOffers: 0,
    pendingInvoices: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          customersResponse,
          projectsResponse,
          timeEntriesResponse,
          reportsResponse,
          offersResponse,
          invoicesResponse
        ] = await Promise.all([
          customersAPI.getAll(),
          projectsAPI.getAll(),
          timeEntriesAPI.getByUser(),
          reportsAPI.getAll(),
          offersAPI.getAll(),
          invoicesAPI.getAll()
        ]);
        
        setStats({
          customers: customersResponse.data.customers.length,
          projects: projectsResponse.data.projects.length,
          activeTimeEntries: timeEntriesResponse.data.timeEntries.filter(entry => !entry.end_time).length,
          pendingReports: reportsResponse.data.reports.filter(report => report.status === 'draft').length,
          pendingOffers: offersResponse.data.offers.filter(offer => offer.status === 'draft').length,
          pendingInvoices: invoicesResponse.data.invoices.filter(invoice => invoice.status === 'pending').length
        });
        setError('');
      } catch (err) {
        setError('Fehler beim Laden der Dashboard-Daten');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin">
          <Clock className="h-12 w-12 text-primary-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <CircleAlert className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-xl text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <div className="text-xs sm:text-sm text-gray-500">
            Aktualisiert: {new Date().toLocaleString('de-CH', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          <StatCard 
            icon={Users}
            title="Kunden"
            value={stats.customers}
            link="/kunden"
            color="text-blue-500"
          />

          <StatCard 
            icon={Briefcase}
            title="Aufträge"
            value={stats.projects}
            link="/projekte"
            color="text-green-500"
          />

          <StatCard 
            icon={Clock}
            title="Aktive Zeiterfassungen"
            value={stats.activeTimeEntries}
            link="/zeiterfassung"
            color="text-yellow-500"
          />

          <StatCard 
            icon={FileText}
            title="Ausstehende Rapporte"
            value={stats.pendingReports}
            link="/rapporte"
            color="text-purple-500"
          />

          <StatCard 
            icon={File}
            title="Ausstehende Rechnungen"
            value={stats.pendingInvoices}
            link="/rechnungen"
            color="text-red-500"
          />

          <StatCard 
            icon={Folder}
            title="Ausstehende Offerten"
            value={stats.pendingOffers}
            link="/offerten"
            color="text-indigo-500"
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
